import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { t } from '../../i18n'
import {
  executeCommand as executeCommandApi,
  fetchCommands,
  fetchSession,
  finishSession as finishSessionApi,
  startSession as startSessionApi,
} from './api'
import { CommandButton, CommandsPayload, SessionPayload } from './types'
import CommandsLayout from './commands_layout/CommandsLayout'
import {
  SESSION_FINISH_COMMAND,
  SESSION_START_COMMAND,
  isOperatorSession,
  sessionTypeName,
} from './sessionTypes'
import CommandExecutionContext from './CommandExecutionContext'
import FlashNotification from '../shared/flashNotification'
import Modal from '../shared/modal'
import { errorText } from './errors'
import { LocalizationContext } from '../shared/contexts'
import { formatTenantTime } from '../../utils/formatTenantDateTime'

interface Toast { type: 'success' | 'danger'; message: string }

const scope = 'controls'

const sessionActionButton = (command: string, humanNameKey: string): CommandButton => ({
  name: command,
  command,
  params: {},
  human_name: t(humanNameKey, { scope }),
  group: 'robot_and_gripper',
  available: true,
  requires_service_session: false,
  supports_queue: false,
})

const Actions = () => {
  const [session, setSession] = useState<SessionPayload>({ state: null })
  const [sessionLoaded, setSessionLoaded] = useState<boolean>(false)
  const [countdown, setCountdown] = useState<number>(0)
  const [commands, setCommands] = useState<CommandsPayload | null>(null)
  const [sessionBusy, setSessionBusy] = useState<boolean>(false)
  const [busyCommands, setBusyCommands] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<Toast | null>(null)
  const [pendingCommand, setPendingCommand] = useState<CommandButton | null>(null)
  const localization = useContext(LocalizationContext)

  useEffect(() => {
    if (!toast || toast.type === 'danger') return
    const id = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(id)
  }, [toast])

  const applySessionResponse = useCallback((data: SessionPayload) => {
    setSession(data)
    setSessionLoaded(true)
    setCountdown(data.retry_after && data.retry_after > 0 ? data.retry_after : 0)
  }, [])

  const refreshCommands = useCallback(async () => {
    const data = await fetchCommands()
    setCommands(data)
  }, [])

  const refreshSession = useCallback(async () => {
    applySessionResponse(await fetchSession())
  }, [applySessionResponse])

  const startSession = useCallback(async () => {
    setSessionBusy(true)
    try {
      applySessionResponse(await startSessionApi())
    } catch (error: any) {
      setToast({ type: 'danger', message: errorText(error, 'errors.start_session') })
    } finally {
      setSessionBusy(false)
    }
  }, [applySessionResponse])

  const finishSession = useCallback(async () => {
    setSessionBusy(true)
    try {
      await finishSessionApi()
      setSession({ state: null })
      setCountdown(0)
    } catch (error: any) {
      setToast({ type: 'danger', message: errorText(error, 'errors.end_session') })
    } finally {
      setSessionBusy(false)
    }
  }, [])

  const requestStartSession = useCallback(() => {
    setPendingCommand(sessionActionButton(SESSION_START_COMMAND, 'start_session'))
  }, [])

  const requestFinishSession = useCallback(() => {
    setPendingCommand(sessionActionButton(SESSION_FINISH_COMMAND, 'end_session'))
  }, [])

  const requestCommand = useCallback((button: CommandButton) => {
    setPendingCommand(button)
  }, [])

  const runPendingCommand = useCallback(async () => {
    const button = pendingCommand
    if (!button) return
    setPendingCommand(null)

    if (button.command === SESSION_START_COMMAND) return startSession()
    if (button.command === SESSION_FINISH_COMMAND) return finishSession()

    setBusyCommands(prev => new Set(prev).add(button.name))
    try {
      // A queueable command is always run ASAP from this page; Admin → Settings keeps the choice.
      const { kind } = await executeCommandApi(button, button.supports_queue ? { asap: true } : {})
      const key = kind === 'scheduled' ? 'command_scheduled' : 'command_executed'
      setToast({ type: 'success', message: t(key, { scope, command: button.human_name }) })
    } catch (error: any) {
      setToast({ type: 'danger', message: errorText(error, 'errors.execute_command') })
    } finally {
      // A failed command can still change server state (e.g. emergency stop engages
      // even when the device disable is unconfirmed) — always re-sync the buttons.
      await Promise.all([refreshCommands(), refreshSession()]).catch(() => {})
      setBusyCommands(prev => {
        const next = new Set(prev)
        next.delete(button.name)
        return next
      })
    }
  }, [pendingCommand, refreshCommands, refreshSession, startSession, finishSession])

  const isBusy = useCallback((name: string) => busyCommands.has(name), [busyCommands])

  const blockingSessionType = commands?.blocking_session_type ?? null

  const executionValue = useMemo(
    () => ({ requestCommand, isBusy, blockingSessionType }),
    [requestCommand, isBusy, blockingSessionType],
  )

  useEffect(() => {
    fetchSession().then(applySessionResponse)
  }, [applySessionResponse])

  useEffect(() => {
    refreshCommands()
  }, [session.state, session.service_type, refreshCommands])

  // Intake can be closed server-side (RobotExecutionStoppedAlertJob), so re-sync the buttons
  // when the session poll reports a stop state the commands payload does not know about yet.
  useEffect(() => {
    if (session.orders_stopped === undefined || commands === null) return
    if (session.orders_stopped !== commands.orders_stopped) refreshCommands()
  }, [session.orders_stopped, commands, refreshCommands])

  useEffect(() => {
    if (session.state !== 'pending' || countdown <= 0) return
    const id = setInterval(() => setCountdown(c => Math.max(c - 1, 0)), 1000)
    return () => clearInterval(id)
  }, [session.state, countdown])

  // Only our own pending session auto-retries — a pending Service app session must be
  // resumed there, and the server would reject a start against it anyway.
  useEffect(() => {
    if (session.state === 'pending' && isOperatorSession(session) && countdown === 0 && !sessionBusy) {
      startSession()
    }
  }, [session.state, session.service_type, countdown, sessionBusy, startSession])

  // Poll the session so a Service app session started elsewhere locks this page down without
  // a reload. Tightens to 5s while a pause awaits confirmation, so the paused state the
  // scheduler confirms reaches the page (and the status band) without delay.
  //
  // Never while pending: `show` omits retry_after, so a poll would zero the countdown and
  // trip the retry effect every cycle. Nothing can start elsewhere during our pending session
  // anyway — the Service app rejects a start against a pending session of another type.
  useEffect(() => {
    if (session.state === 'pending') return
    const id = setInterval(() => { refreshSession() }, session.robot_pausing ? 5000 : 15000)
    return () => clearInterval(id)
  }, [session.state, session.robot_pausing, refreshSession])

  return (
    <div className="py-3">
      {commands?.emergency_stopped && (
        <div className="alert alert-danger fw-bold" role="alert">
          {commands.emergency_restart_requested
            ? t('emergency_restart_pending', { scope })
            : t('emergency_stop_active', { scope })}
        </div>
      )}

      {commands?.orders_stopped && (
        <div className={`alert fw-bold ${session.execution_faulted_since ? 'alert-danger' : 'alert-warning'}`} role="alert">
          {session.execution_faulted_since
            ? t('orders_stopped_robot_not_executing', {
              scope, since: formatTenantTime(session.execution_faulted_since, localization),
            })
            : t('orders_stopped_active', { scope })}
        </div>
      )}

      {blockingSessionType && (
        <div className="alert alert-warning fw-bold" role="alert">
          {t('session_blocked_banner', { scope, session_type: sessionTypeName(blockingSessionType) })}
        </div>
      )}

      <CommandExecutionContext.Provider value={executionValue}>
        <CommandsLayout
          commands={commands}
          session={session}
          sessionLoaded={sessionLoaded}
          countdown={countdown}
          sessionBusy={sessionBusy}
          onStartSession={requestStartSession}
          onFinishSession={requestFinishSession}
        />
      </CommandExecutionContext.Provider>

      {pendingCommand && pendingCommand.command === 'emergency_stop' && (
        <Modal
          centered
          headerText={pendingCommand.human_name}
          onClose={() => setPendingCommand(null)}
          footerContent={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPendingCommand(null)}>
                {t('cancel', { scope })}
              </button>
              <button type="button" className="btn btn-danger" onClick={() => runPendingCommand()}>
                {t('confirm_emergency_stop_button', { scope })}
              </button>
            </>
          }
        >
          {t('confirm_emergency_stop', { scope })}
        </Modal>
      )}

      {pendingCommand && pendingCommand.command !== 'emergency_stop' && (
        <Modal
          centered
          headerText={t('confirm_execute_title', { scope })}
          onClose={() => setPendingCommand(null)}
          footerContent={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPendingCommand(null)}>
                {t('cancel', { scope })}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => runPendingCommand()}>
                {t('confirm_execute_button', { scope })}
              </button>
            </>
          }
        >
          {t('confirm_execute', { scope, name: pendingCommand.human_name })}
        </Modal>
      )}

      {toast && (
        <FlashNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default Actions
